import "./Calculator.scss";
import InputRow from "./InputRow";
import { OptionRow } from "./OptionRow";
import { SubtitleRow } from "./SubtitleRow";
import config from "./calculatorConfig";
import { useEffect, useState } from "react";
import React from "react";
import utils from "./utils";

const calcTarifGo = (g, loc) => {
  if (g < config.minGo) return -1;
  let pergo = Number.MAX_VALUE;
  for (const go in config.pricingGo) {
    const nbGo = parseInt(go);
    const price = config.pricingGo[go][loc] / nbGo;
    if (nbGo <= g && price < pergo) {
      pergo = price;
    }
  }
  return Math.round(pergo * 100) / 100;
};

const calcTotal = (datas, pergo) => {
  if (datas.iGo < config.minGo)
    throw new Error(
      `Le quota de sauvegade ne peut pas être inférieur à ${config.minGo} Go`
    );
  if (datas.opt_base < 1)
    throw new Error(
      `Vous devez selectionner au minimum une protection de base par poste.`
    );
  let total = pergo * parseInt(datas.iGo);
  for (const k in datas) {
    if (k.indexOf("opt_") !== 0) continue;
    const optKey = k.split("_")[1];
    const count = parseInt(datas[k]);
    const unitPrice = config.pricingLic[optKey].price;
    total += count * unitPrice;
  }
  return total;
};

const preloadImg = (path) => {
  [
    "folder.svg",
    "hard-drive.svg",
    "microsoft-exchange.svg",
    "microsoft-sql-server.png",
    "mongodb.svg",
    "o365.svg",
    "windows.svg",
  ].forEach((i) => {
    const __img = new Image();
    __img.src = path + "/" + i;
  });
};

const generateMsgContent = (datas, totalPrice) => {
  let txt = "Demande de devis sauvegarde en ligne : &#010;&#010;";
  txt += `  - Emplacement de la sauvegarde : ${datas.location} &#010;`;
  txt += `  - Quantité à sauvegarder : ${datas.iGo} Go &#010;`;
  for (const k in datas) {
    if (k.indexOf("opt_") !== 0) continue;
    const count = parseInt(datas[k]);
    if (count < 1) continue;
    const optKey = k.split("_")[1];
    const label = config.pricingLic[optKey].label;
    txt += `  - ${label} : ${count} &#010;`;
  }
  txt += " &#010;Total mensuel : " + utils.intToEuros(totalPrice);
  return txt;
};

function Calculator({ imgPath, postUrl, postFieldName }) {
  const [formDatas, setFormDatas] = useState({
    location: "blackblaze",
    iGo: 50,
  });
  const [priceGo, setPriceGo] = useState(0);
  const [pricePerGo, setPricePerGo] = useState(0);
  const [showOptions, setShowOptions] = useState(true);
  const [totalPrice, setTotalPrice] = useState(-1);
  const [error, setError] = useState(null);
  const [formMsgData, setFormMsgData] = useState(null);
  const [helpPopupOpened, setHelpPopupOpened] = useState(false);

  const fieldValueChanged = (name, val) => {
    setFormDatas((datas) => ({ ...datas, [name]: val }));
  };

  let minBbGo = Number.MAX_VALUE;
  let minAWSGo = Number.MAX_VALUE;
  for (const go in config.pricingGo) {
    const bbpergo = config.pricingGo[go].blackblaze / parseInt(go);
    if (bbpergo < minBbGo) minBbGo = bbpergo;
    const awspergo = config.pricingGo[go].aws / parseInt(go);
    if (awspergo < minAWSGo) minAWSGo = awspergo;
  }

  useEffect(() => {
    setError(null);
    let pergo, total;
    try {
      pergo = calcTarifGo(formDatas.iGo, formDatas.location);
      total = calcTotal(formDatas, pergo);
    } catch (e) {
      setError(e.message);
      return;
    } finally {
      setPricePerGo(pergo);
      setPriceGo(Math.round(formDatas.iGo * pergo * 100) / 100);
    }
    setTotalPrice(total);
    setFormMsgData(generateMsgContent(formDatas, total));
  }, [formDatas]);

  useEffect(() => {
    preloadImg(imgPath);
  }, [imgPath]);

  return (
    <form
      method="GET"
      action={postUrl}
      className="beevee-backup-calculator-container"
    >
      {helpPopupOpened && (
        <>
          <div class="datacenter-help-popup-background"></div>
          <div class="datacenter-help-popup">
            <div class="close-row">
              <span class="close-button" onClick={() => setHelpPopupOpened(false)}>fermer</span>
            </div>
            <h4>Backblaze - Amsterdam</h4>
            <p>
              Les données sont stockées dans un datacenter unique géré par la
              société Backblaze à Amsterdam.
              <br />
              Le stockage est répliqué en local sur une infrastructure à haute
              résilience et une durabilité annoncée à 99.999999999% (11 9’s).
            </p>
            <h4>AWS - Paris</h4>
            <p>
              Les données sont stockées sur l'infrastructure de la société AWS
              en région parisienne. <br />
              Les données sont répliqués entre 3 datacenters à différents
              emplacements géographiques autour de Paris pour une disponibilité
              renforcée et une durabilité annoncée à 99.999999999% (11 9’s).
            </p>
          </div>
        </>
      )}
      <div className="input-row">
        <label>
          Emplacement de votre sauvegarde &nbsp;
          <img class="help-button" onClick={() => setHelpPopupOpened(true)} src={imgPath + "/help-icon.png"} />
        </label>
        <select
          className="location"
          onChange={(e) => fieldValueChanged("location", e.target.value)}
        >
          <option value="blackblaze">
            Backblaze - Amsterdam (à partir de {minBbGo} € / Go)
          </option>
          <option value="aws">
            AWS - Paris (à partir de {minAWSGo} € / Go)
          </option>
        </select>
      </div>
      <InputRow label="Quota de données à sauvegarder (données originales)">
        <div className="quota-row">
          <div class="input-go">
            <input
              type="number"
              min="50"
              step="50"
              defaultValue="50"
              onChange={(e) => fieldValueChanged("iGo", e.target.value)}
            />
            &nbsp;&nbsp;
            <b>Go</b>
          </div>
          <div className="price">
            (
            {pricePerGo > -1
              ? utils.intToEuros(pricePerGo) +
                " par Go =\u00A0" +
                utils.intToEuros(priceGo)
              : "Veuillez sélectionner au minimum " + config.minGo + " Go"}
            )
          </div>
        </div>
      </InputRow>
      <SubtitleRow title="Protection de base (par poste)" />
      <OptionRow
        name="opt_base"
        title="Sauvegarde de fichiers et dossiers"
        subtitle="Windows / Mac / Linux, inclus bases MySQL"
        price={config.pricingLic.base}
        img={imgPath + "/folder.svg"}
        min="1"
        valueChanged={fieldValueChanged}
      />
      <SubtitleRow
        title="Options supplémentaires"
        click={() => {
          setShowOptions(!showOptions);
        }}
      />
      {showOptions && (
        <React.Fragment>
          <OptionRow
            name="opt_disk"
            title="Image disque"
            price={config.pricingLic.disk}
            img={imgPath + "/hard-drive.svg"}
            valueChanged={fieldValueChanged}
          />
          <OptionRow
            name="opt_MS365"
            title="Microsoft 365"
            subtitle="email, calendriers, contacts, OneDrive, Sharepoint, Teams"
            price={config.pricingLic.MS365}
            img={imgPath + "/o365.svg"}
            valueChanged={fieldValueChanged}
          />
          <OptionRow
            name="opt_hv"
            title="Hyper-V"
            price={config.pricingLic.hv}
            img={imgPath + "/windows.svg"}
            valueChanged={fieldValueChanged}
          />
          <OptionRow
            name="opt_exch"
            title="Serveur Exchange"
            price={config.pricingLic.exch}
            img={imgPath + "/microsoft-exchange.svg"}
            valueChanged={fieldValueChanged}
          />
          <OptionRow
            name="opt_mssql"
            title="Bases de données SQL Server"
            price={config.pricingLic.mssql}
            img={imgPath + "/microsoft-sql-server.png"}
            valueChanged={fieldValueChanged}
          />
          <OptionRow
            name="opt_mongo"
            title="Bases de données MongoDB"
            price={config.pricingLic.mongo}
            img={imgPath + "/mongodb.svg"}
            valueChanged={fieldValueChanged}
          />
        </React.Fragment>
      )}
      <hr className="dark" />
      <div className="result-footer">
        <div className="">
          Abonnement mensuel (HT) : &nbsp;
          {error ? (
            <div className="error">{error}</div>
          ) : (
            <React.Fragment>
              <b className="price">
                {totalPrice > -1 ? utils.intToEuros(totalPrice) : "-"}
              </b>
              <input type="hidden" name={postFieldName} value={formMsgData} />
              <button type="submit" className="devis">
                Demander mon devis
              </button>
            </React.Fragment>
          )}
        </div>
      </div>
    </form>
  );
}

export default Calculator;
